import { useCallback, useContext } from "react";
import copyToClipboard from "copy-to-clipboard";
import type { TemporalState } from "zundo";
import { useStore as useZustandStore } from "zustand";
import { t } from "@/i18n-js/instance";
import { BuilderUIContext } from "@circle-react-shared/BuilderUI/Provider/BuilderUIProvider";
import type { BuilderUIState } from "@circle-react-shared/BuilderUI/Store/types";
import { findBlockPathById } from "@circle-react-shared/BuilderUI/Store/utils/blockUpdateUtils";
import { useToast } from "@circle-react-shared/uikit/ToastV2";

export const useBuilderStoreErrorMessage =
  "useBuilderStore must be used within a <BuilderUI.Provider /> component";

export const useBuilderStore = <T>(
  selector: (state: BuilderUIState) => T,
): T => {
  const store = useContext(BuilderUIContext);
  if (!store) {
    throw new Error(useBuilderStoreErrorMessage);
  }
  return useZustandStore(store, selector);
};

export const useSelectedBlock = () => {
  const toast = useToast();

  // Single subscription for all dynamic data
  const selectedBlockData = useBuilderStore(state => {
    const value = state.getSelectedBlock();
    return {
      selectedBlock: value?.block || null,
      meta: value?.meta || null,
      isDeletable: state.getCanDeleteSelectedBlock(),
      isDuplicateable: state.getCanDuplicateSelectedBlock(),
      canMoveUp: state.getCanMoveSelectedBlock("up"),
      canMoveDown: state.getCanMoveSelectedBlock("down"),
    };
  });

  // Static functions that don't change
  const moveSelectedBlock = useBuilderStore(state => state.moveSelectedBlock);
  const removeSelectedBlock = useBuilderStore(
    state => state.removeSelectedBlock,
  );
  const setSelectedBlockPath = useBuilderStore(
    state => state.setSelectedBlockPath,
  );
  const duplicateSelectedBlock = useBuilderStore(
    state => state.duplicateSelectedBlock,
  );
  const resetBlockStyle = useBuilderStore(state => state.resetBlockStyle);

  const copyBlockId = useCallback(() => {
    const blockId = selectedBlockData.selectedBlock?.id;
    if (!blockId) {
      return null;
    }

    const didCopy = copyToClipboard(blockId);
    if (didCopy) {
      toast.success(t("builder_ui.editor.block_id_copied"));
    }
    return blockId;
  }, [selectedBlockData.selectedBlock?.id, toast]);

  return {
    selectedBlock: selectedBlockData.selectedBlock,
    meta: selectedBlockData.meta,
    isDeletable: selectedBlockData.isDeletable,
    isDuplicateable: selectedBlockData.isDuplicateable,
    canMoveUp: selectedBlockData.canMoveUp,
    canMoveDown: selectedBlockData.canMoveDown,
    moveSelectedBlock,
    removeSelectedBlock,
    setSelectedBlockPath,
    duplicateSelectedBlock,
    resetBlockStyle,
    copyBlockId,
  };
};

export const useBlock = (blockId: string) => {
  const toast = useToast();

  const { blockData, store } = useBuilderStore(state => {
    const value = state.getBlock(blockId);
    return {
      blockData: {
        block: value?.block || null,
        meta: value?.meta || null,
        canMoveUp: state.getCanMoveBlock(blockId, "up"),
        canMoveDown: state.getCanMoveBlock(blockId, "down"),
      },
      store: state,
    };
  });

  const withSelection = (action: () => void) => {
    const prev = store.selectedBlockPath;
    const path = findBlockPathById(store.getBlocks(), blockId);
    if (path) {
      store.setSelectedBlockPath(path);
      action();
      if (prev) store.setSelectedBlockPath(prev);
    }
  };

  return {
    ...blockData,
    moveBlock: (direction: "up" | "down" = "down") =>
      withSelection(() => store.moveSelectedBlock(direction)),
    removeBlock: () => withSelection(() => store.removeSelectedBlock()),
    duplicateBlock: () => withSelection(() => store.duplicateSelectedBlock()),
    resetBlockStyle: () => withSelection(() => store.resetBlockStyle()),
    copyBlockId: () => {
      if (copyToClipboard(blockId)) {
        toast.success(t("builder_ui.editor.block_id_copied"));
      }
      return blockId;
    },
    renameBlock: (newName: string, blockId: string) =>
      withSelection(() => {
        store.renameBlock(newName, blockId);
      }),
    toggleBlockVisibility: () => {
      store.toggleBlockVisibility(blockId);
    },
  };
};

export const useBuilderHistory = <T>(
  selector: (state: TemporalState<Pick<BuilderUIState, "pages">>) => T,
): T => {
  const store = useContext(BuilderUIContext);
  if (!store) {
    throw new Error(useBuilderStoreErrorMessage);
  }
  return useZustandStore(store.temporal, selector);
};
